import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useStripe } from 'contexts/stripe';
import { useViewport } from 'contexts/viewport';
import moment from 'moment';
import { useEffect, useState } from 'react';
import userService from 'services/user';
import {
    Box,
    Button,
    Dropdown,
    Icon,
    IconButton,
    Link,
    Loading,
    Paper,
    Stack,
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tag,
    Text,
} from 'shell/components';
import { getCardSvg } from 'utils/helper';

const Billing = ({ setSelected }) => {
    const { customer, subscription, entitlements } = useApp();
    const { currentUser } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const {
        setUpdateCardDialog,
        initUpgradeFlow,
        cancelAtPeriodEnd,
        undoCancelAtPeriodEnd,
        detachPaymentMethod,
        undoScheduleSubscriptionBillingPeriodChange,
    } = useStripe();
    const { message } = useSnackbar();
    const { device } = useViewport();

    const [subscriptionSchedule, setSubscriptionSchedule] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState();
    const [paymentHistory, setPaymentHistory] = useState(null);
    const [confirmCancelSubscription, setConfirmCancelSubscription] = useState(false);
    const [cancellationLoading, setCancellationLoading] = useState(false);
    const [reactivationLoading, setReactivationLoading] = useState(false);

    const onClickCancelSubscription = () => {
        setDialog({
            open: true,
            message: 'Cancel Your Subscription?',
            description:
                'Your subscription will be cancelled immediately, but paid features will be available until the end of your billing period.',
            note: '',
            variant: 'destructive',
            actionButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                setCancellationLoading(true);
                closeDialog();
                await cancelAtPeriodEnd();
                setConfirmCancelSubscription(false);
                setCancellationLoading(false);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const onClickReactivateSubscription = () => {
        if (!paymentMethod) {
            setUpdateCardDialog((current) => ({ ...current, open: true, reactivate: true }));
        } else {
            setDialog({
                open: true,
                message: 'Reactivate Starter Plan Subscription?',
                description:
                    'Are you sure you want to reactivate your Starter Plan subscription? You’ll retain full access without interruption, and your billing will continue as scheduled.',
                note: '',
                variant: 'primary',
                actionButtonText: 'Reactivate',
                cancelButtonText: 'Cancel',
                onAction: async () => {
                    setReactivationLoading(true);
                    closeDialog();
                    await undoCancelAtPeriodEnd();
                    setReactivationLoading(false);
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const onClickCancelSchedule = () => {
        setDialog({
            open: true,
            message: `Continue with ${subscription.plan.interval === 'year' ? 'Annual' : 'Monthly'} Billing?`,
            description: `Your plan is set to switch to ${subscription.plan.interval === 'year' ? 'monthly' : 'annual'} billing on ${moment(new Date(subscription.current_period_end * 1000).getTime()).format('MMM D, YYYY')}. To continue billing ${subscription.plan.interval === 'year' ? 'annually' : 'monthly'}, please confirm this change.`,
            note: '',
            variant: 'primary',
            actionButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            onAction: async () => {
                closeDialog();
                await undoScheduleSubscriptionBillingPeriodChange();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleSelect = ({ key }) => {
        if (key === 'edit') {
            setUpdateCardDialog((current) => ({ ...current, open: true, reactivate: false }));
        }
        if (key === 'delete') {
            setDialog({
                open: true,
                message: 'Delete This Payment Method?',
                description:
                    'If you delete this payment method, you’ll need to add a new one if you decide to upgrade to a paid plan in the future.',
                note: '',
                variant: 'destructive',
                actionButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                onAction: async () => {
                    detachPaymentMethod();
                    setPaymentMethod(null);
                    closeDialog();
                    message.success('Your payment details were removed successfully.');
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const renderCurrentSubscription = () => {
        if (subscription.schedule && !subscriptionSchedule) {
            return (
                <Stack sx={{ minHeight: '40px' }}>
                    <Loading sx={{ m: 'auto' }} />
                </Stack>
            );
        }

        if (subscription.status === 'trialing') {
            return (
                <Stack spacing={4}>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            <Text variant={'sh2'}>Starter Plan</Text>
                            <Tag type={'general'} value={'trial'} />
                        </Stack>
                        <Button onClick={initUpgradeFlow}>Upgrade</Button>
                    </Stack>
                    <Text>
                        Your free trial of the Starter Plan ends on{' '}
                        {moment(new Date(subscription.trial_end * 1000).getTime()).format('MMM D, YYYY')}. After the
                        trial, your account will automatically switch to the Free Plan.
                    </Text>
                </Stack>
            );
        }

        if (subscription.cancel_at_period_end) {
            return reactivationLoading ? (
                <Stack sx={{ minHeight: '40px' }}>
                    <Loading sx={{ m: 'auto' }} />
                </Stack>
            ) : (
                <Stack alignItems={'flex-start'} spacing={5}>
                    <Text>
                        Your Starter Plan subscription has been successfully canceled. You’ll continue to have access to
                        all Starter Plan features until the end of your current billing cycle on{' '}
                        {moment(new Date(subscription.cancel_at * 1000).getTime()).format('MMM D, YYYY')}. After that,
                        you’ll be switched to the Free Plan.
                    </Text>
                    <Button onClick={onClickReactivateSubscription}>Reactivate Subscription</Button>
                </Stack>
            );
        }

        if (!entitlements.length) {
            return (
                <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                    <Stack spacing={3}>
                        <Text variant={'sh2'}>Free Plan</Text>
                        <Text component={'p'} variant={'h4'}>
                            Free
                        </Text>
                    </Stack>
                    <Button onClick={initUpgradeFlow}>Upgrade</Button>
                </Stack>
            );
        }

        if (entitlements.length) {
            const renewalDate = moment(new Date(subscription.current_period_end * 1000).getTime());
            const expiryDate = moment(
                new Date(`${paymentMethod?.card?.exp_month}/1/${paymentMethod?.card?.exp_year}`).getTime()
            ).endOf('month');
            const isGoingToExpire = renewalDate.isAfter(expiryDate);

            return (
                <Stack spacing={5}>
                    <Stack
                        direction={device === 'mobile' ? 'column' : 'row'}
                        justifyContent={'space-between'}
                        spacing={2}
                    >
                        <Stack justifyContent={'space-between'}>
                            <Text variant={'sh2'}>Starter Plan</Text>
                            <Text component={'p'} variant={'h4'}>
                                ${subscription.plan.amount / 100}
                                <Text sx={{ verticalAlign: 'text-top' }} component={'span'}>
                                    {' '}
                                    {subscription.currency.toUpperCase()}{' '}
                                </Text>
                                <Text sx={{ verticalAlign: 'text-top' }} color={'light'} component={'span'}>
                                    /{subscription.plan.interval}
                                </Text>
                            </Text>
                            {!subscriptionSchedule ? (
                                <Text variant={'p3'}>
                                    Your subscription renews{' '}
                                    {moment(new Date(subscription.current_period_end * 1000).getTime()).format(
                                        'MMM D, YYYY'
                                    )}
                                </Text>
                            ) : (
                                <Stack spacing={3}>
                                    <Text variant={'p3'}>
                                        Your plan is currently billed{' '}
                                        {subscription.plan.interval === 'year' ? 'annually' : 'monthly'} and will switch
                                        to {subscription.plan.interval === 'year' ? 'monthly' : 'annual'} billing at{' '}
                                        {subscription.plan.interval === 'year' ? '$12/month' : '$120/year ($10/month)'}{' '}
                                        when your current billing period ends on{' '}
                                        {moment(new Date(subscription.current_period_end * 1000).getTime()).format(
                                            'MMM D, YYYY'
                                        )}
                                        .
                                    </Text>
                                    <Button
                                        sx={{ width: 'fit-content' }}
                                        size={'small'}
                                        variant={'borderless'}
                                        onClick={onClickCancelSchedule}
                                    >
                                        Continue with {subscription.plan.interval === 'year' ? 'annual' : 'monthly'}{' '}
                                        billing
                                    </Button>
                                </Stack>
                            )}
                        </Stack>
                        <Stack sx={{ minWidth: '161px' }} spacing={3}>
                            <Button onClick={initUpgradeFlow}>Change Plan</Button>
                            <Button variant={'secondary'} onClick={() => setConfirmCancelSubscription(true)}>
                                Cancel Subscription
                            </Button>
                        </Stack>
                    </Stack>
                    {isGoingToExpire && (
                        <Stack
                            sx={(theme) => ({
                                borderRadius: '8px',
                                p: 3,
                                background: theme.palette.ensightful.warning[100],
                                color: theme.palette.ensightful.warning[800],
                            })}
                            alignItems={'center'}
                            direction={'row'}
                            spacing={2}
                        >
                            <Icon
                                sx={(theme) => ({
                                    borderRadius: '50%',
                                    width: '16px',
                                    height: '16px',
                                    padding: theme.spacing(0.75),
                                    background: theme.palette.ensightful.warning[800],
                                    color: theme.palette.ensightful.shades[0],
                                })}
                                kind={'error'}
                            />
                            <Text color={'inherit'}>
                                Your payment method is about to expire. Please update your payment details to prevent
                                any disruption in your subscription.
                            </Text>
                        </Stack>
                    )}
                </Stack>
            );
        }
    };

    const renderPaymentMethod = () => {
        if (typeof paymentMethod === 'undefined') {
            return (
                <Stack sx={{ minHeight: '40px' }}>
                    <Loading sx={{ m: 'auto' }} />
                </Stack>
            );
        }

        if (subscription.status === 'trialing') {
            return (
                <Stack>
                    <Text color={'light'}>
                        No payment information is needed for the Free Plan or the Starter Plan Free Trial. If you
                        upgrade to a paid plan, your payment details will be stored here.
                    </Text>
                </Stack>
            );
        }

        if (!paymentMethod) {
            return (
                <Stack>
                    <Text color={'light'}>
                        You don’t have a payment method saved. To add a card and unlock all of TimelyGrader’s features,
                        please join a paid plan.
                    </Text>
                </Stack>
            );
        }

        const cardSvg = getCardSvg(paymentMethod.card.brand);
        const today = moment(new Date().getTime());
        const expiryDate = moment(
            new Date(`${paymentMethod.card.exp_month}/1/${paymentMethod.card.exp_year}`).getTime()
        ).endOf('month');
        const isExpired = today.isAfter(expiryDate);

        return (
            <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    {cardSvg && <Box sx={{ mx: -8, my: -2, scale: '0.5' }}>{cardSvg}</Box>}
                    <Stack spacing={1}>
                        <Text sx={{ textTransform: 'capitalize' }} weight={'semibold'}>
                            {paymentMethod.card.brand} •••• {paymentMethod.card.last4}
                        </Text>
                        {isExpired ? (
                            <Text
                                sx={(theme) => ({
                                    borderRadius: '4px',
                                    width: 'fit-content',
                                    px: 2,
                                    py: 1,
                                    background: theme.palette.ensightful.error[50],
                                })}
                                color={'error'}
                            >
                                Expired {paymentMethod.card.exp_month}/
                                {paymentMethod.card.exp_year.toString().substring(2, 4)}
                            </Text>
                        ) : (
                            <Text color={'light'}>
                                Expires {paymentMethod.card.exp_month}/
                                {paymentMethod.card.exp_year.toString().substring(2, 4)}
                            </Text>
                        )}
                    </Stack>
                </Stack>
                <Dropdown
                    id={'payment-method-dropdown'}
                    options={[
                        { key: 'edit', value: 'Update', icon: 'edit' },
                        ...(subscription.cancel_at_period_end || !entitlements.length
                            ? [{ key: 'delete', value: 'Delete', icon: 'delete' }]
                            : []),
                    ]}
                    rootComponent={
                        <IconButton aria-label={'menu'} kind={'moreHorizontal'} size={'medium'} variant={'square'} />
                    }
                    onSelect={handleSelect}
                />
            </Stack>
        );
    };

    const renderPaymentHistory = () => {
        if (!paymentHistory) {
            return (
                <Stack sx={{ minHeight: '40px' }}>
                    <Loading sx={{ m: 'auto' }} />
                </Stack>
            );
        }

        if (!paymentHistory.length) {
            return (
                <Stack>
                    <Text color={'light'}>
                        You haven't made any payments in the last 12 months. If you upgrade to a paid plan you’ll see
                        billing details here.
                    </Text>
                </Stack>
            );
        }

        return (
            <Stack>
                <Text>Showing all payments within the last 12 months.</Text>
                <Table size={'small'}>
                    <TableHead>
                        <TableRow>
                            <CustomTableCell>Date</CustomTableCell>
                            <CustomTableCell>Card No.</CustomTableCell>
                            <CustomTableCell>Amount</CustomTableCell>
                        </TableRow>
                    </TableHead>
                    <CustomTableBody>
                        {paymentHistory.map((paymentIntent) => (
                            <TableRow key={paymentIntent.id}>
                                <CustomTableCell>
                                    {moment(new Date(paymentIntent.created * 1000).getTime()).format('MMM D, YYYY')}
                                </CustomTableCell>
                                <CustomTableCell sx={{ textTransform: 'capitalize' }}>
                                    {paymentIntent.payment_method.card.brand} ····{' '}
                                    {paymentIntent.payment_method.card.last4}
                                </CustomTableCell>
                                <CustomTableCell sx={{ textTransform: 'uppercase' }}>
                                    ${(paymentIntent.amount / 100).toFixed(2)} {paymentIntent.currency.toUpperCase()}
                                </CustomTableCell>
                            </TableRow>
                        ))}
                    </CustomTableBody>
                </Table>
            </Stack>
        );
    };

    useEffect(() => {
        if (currentUser && customer && subscription) {
            (async () => {
                if (subscription.schedule) {
                    const subscriptionSchedule = await userService.getUserSubscriptionSchedule(
                        currentUser.id,
                        customer.id,
                        subscription.schedule
                    );
                    setSubscriptionSchedule(subscriptionSchedule);
                } else {
                    setSubscriptionSchedule(null);
                }
            })();
        }
    }, [currentUser, customer, subscription]);

    useEffect(() => {
        if (currentUser && customer) {
            (async () => {
                const paymentMethod = await userService.getUserPaymentMethod(currentUser.id, customer.id);
                setPaymentMethod(paymentMethod);
            })();
            (async () => {
                const paymentIntents = await userService.getUserPaymentIntents(currentUser.id, customer.id);
                setPaymentHistory(paymentIntents.filter((paymentIntent) => Boolean(paymentIntent.payment_method)));
            })();
        }
    }, [currentUser, customer]);

    return !subscription ? (
        <RootStack sx={{ maxWidth: 'unset' }} device={device}>
            <Loading />
        </RootStack>
    ) : (
        <RootStack device={device} spacing={4}>
            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                {device === 'mobile' && (
                    <IconButton
                        aria-label={'back'}
                        kind={'arrowLeft'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => setSelected('')}
                    />
                )}
                <Text component={'h1'} size={'small'} variant={'h6'} weight={'semibold'}>
                    Billing & Plans
                    {confirmCancelSubscription && (
                        <Text sx={{ verticalAlign: 'middle' }} color={'light'} component={'span'}>
                            {' '}
                            <Icon kind={'chevronRight'} /> Cancel subscription
                        </Text>
                    )}
                </Text>
            </Stack>
            {confirmCancelSubscription ? (
                cancellationLoading ? (
                    <Loading sx={{ my: '40vh' }} />
                ) : (
                    <Stack spacing={5}>
                        <Paper variant={'outlined'}>
                            <Stack spacing={3}>
                                <Text variant={'sh2'}>Starter Plan</Text>
                                <Text component={'p'} variant={'h4'}>
                                    ${subscription.plan.amount / 100}
                                    <Text sx={{ verticalAlign: 'text-top' }} component={'span'}>
                                        {' '}
                                        {subscription.currency.toUpperCase()}{' '}
                                    </Text>
                                    <Text sx={{ verticalAlign: 'text-top' }} color={'light'} component={'span'}>
                                        /{subscription.plan.interval}
                                    </Text>
                                </Text>
                                <Text>
                                    Your subscription will be canceled, but will still be available until the end of
                                    your billing period on{' '}
                                    {moment(new Date(subscription.current_period_end * 1000).getTime()).format(
                                        'MMM D, YYYY'
                                    )}
                                    .
                                </Text>
                                <Text>If you change your mind, you can renew your subscription at any time.</Text>
                            </Stack>
                        </Paper>
                        <Stack direction={'row-reverse'} spacing={3}>
                            <Button color={'destructive'} variant={'secondary'} onClick={onClickCancelSubscription}>
                                Cancel Subscription
                            </Button>
                            <Button
                                color={'neutral'}
                                variant={'tertiary'}
                                onClick={() => setConfirmCancelSubscription(false)}
                            >
                                Cancel
                            </Button>
                        </Stack>
                    </Stack>
                )
            ) : (
                <>
                    <CustomStack spacing={1}>
                        <Text variant={'sh2'} weight={'semibold'}>
                            Current Subscription
                        </Text>
                        <Paper variant={'outlined'}>{renderCurrentSubscription()}</Paper>
                    </CustomStack>
                    <CustomStack spacing={1}>
                        <Text variant={'sh2'} weight={'semibold'}>
                            Payment Method
                        </Text>
                        <Paper variant={'outlined'}>{renderPaymentMethod()}</Paper>
                    </CustomStack>
                    <CustomStack spacing={1}>
                        <Text variant={'sh2'} weight={'semibold'}>
                            Payment History
                        </Text>
                        <Paper variant={'outlined'}>{renderPaymentHistory()}</Paper>
                    </CustomStack>
                    <Text color={'light'} variant={'p3'}>
                        If you need assistance with billing or your plan, please contact us at{' '}
                        <Link external href={'mailto:support@timelygrader.ai'}>
                            support@timelygrader.ai
                        </Link>
                        .
                    </Text>
                </>
            )}
        </RootStack>
    );
};

const RootStack = styled(Stack)(({ device, theme }) => {
    return {
        ...((device === 'tablet' || device === 'desktop') && {
            padding: theme.spacing(5),
            maxWidth: '32rem',
            height: 'calc(100vh - 64px - 20px)',
        }),
    };
});

const CustomStack = styled(Stack)(() => {
    return {
        maxWidth: '550px',
    };
});

const CustomTableBody = styled(TableBody)(({ theme }) => {
    return {
        '& tr:nth-child(odd)': {
            background: theme.palette.ensightful.neutral[50],
        },
        '& tr:nth-child(even)': {
            background: theme.palette.ensightful.neutral[100],
        },
    };
});

const CustomTableCell = styled(TableCell)(() => {
    return {
        borderBottom: 'none',
    };
});

export default Billing;
